import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, RotateCcw, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, FileEdit as Edit3, FlaskConical, Lock, Plus, Trash2, TrendingUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface BillingConfigRow {
  id: string;
  key: string;
  value: number;
  section: string;
  label: string;
  description: string;
  unit: string;
  updated_at: string;
  updated_by: string;
}

interface FormulasModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SECTION_META: Record<string, { label: string; description: string; color: string; formula?: string }> = {
  tva: {
    label: 'TVA',
    description: 'Taux de taxe sur la valeur ajoutée appliqués aux postes de facturation.',
    color: 'blue',
    formula: 'Coût TVAC = Coût HTVA × (1 + taux)',
  },
  energie: {
    label: 'Cotisation sur l\'énergie',
    description: 'Composante fixe des droits d\'accise appliquée à tout le volume partagé.',
    color: 'green',
    formula: 'Cotisation = Volume (MWh) × taux (€/MWh)',
  },
  accises_entreprise: {
    label: 'Droits d\'accise — Entreprises',
    description: 'Tarif par tranche de consommation annuelle pour les participants avec numéro d\'entreprise.',
    color: 'amber',
    formula: 'Droit accise = Volume partage (MWh) × taux tranche correspondante',
  },
  accises_particulier: {
    label: 'Droits d\'accise — Particuliers',
    description: 'Tarif par tranche de consommation annuelle pour les participants sans numéro d\'entreprise.',
    color: 'orange',
    formula: 'Droit accise = Volume partage (MWh) × taux tranche correspondante',
  },
  adhesion: {
    label: 'Frais d\'adhésion',
    description: 'Montant annuel facturé une fois par an au mois anniversaire d\'adhésion du participant.',
    color: 'red',
    formula: 'HTVA = TVAC / 1.21 — facturé si mois anniversaire dans la période',
  },
  co2: {
    label: 'Facteur CO₂',
    description: 'Estimation de CO₂ évité affichée dans le dashboard membre (non-financier).',
    color: 'teal',
    formula: 'CO₂ évité (kg) = Volume partage annuel (kWh) × facteur',
  },
  simulateur: {
    label: 'Simulateur d\'économies',
    description: 'Réduction appliquée dans le simulateur public pour estimer les économies potentielles.',
    color: 'purple',
    formula: 'Tarif communauté = Tarif fournisseur × (1 − réduction) — indicatif uniquement',
  },
};

const SECTION_ORDER = ['tva', 'energie', 'accises_entreprise', 'accises_particulier', 'adhesion', 'co2', 'simulateur'];

const COLOR_CLASSES: Record<string, { header: string; badge: string; border: string; dot: string }> = {
  blue:   { header: 'bg-blue-50 border-blue-200',   badge: 'bg-blue-100 text-blue-800',   border: 'border-blue-200',   dot: 'bg-blue-500'   },
  green:  { header: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-800', border: 'border-green-200', dot: 'bg-green-500' },
  amber:  { header: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-800', border: 'border-amber-200', dot: 'bg-amber-500'  },
  orange: { header: 'bg-orange-50 border-orange-200', badge: 'bg-orange-100 text-orange-800', border: 'border-orange-200', dot: 'bg-orange-500' },
  red:    { header: 'bg-red-50 border-red-200',     badge: 'bg-red-100 text-red-800',     border: 'border-red-200',     dot: 'bg-red-500'   },
  teal:   { header: 'bg-teal-50 border-teal-200',   badge: 'bg-teal-100 text-teal-800',   border: 'border-teal-200',   dot: 'bg-teal-500'  },
  purple: { header: 'bg-violet-50 border-violet-200', badge: 'bg-violet-100 text-violet-800', border: 'border-violet-200', dot: 'bg-violet-500' },
};

const SIMULATION_PARAMS = {
  tarifDefaut: [
    { label: 'Tarif fournisseur — Consommateur', value: '160', unit: '€/MWh', description: 'Tarif d\'achat par défaut appliqué aux consommateurs si non renseigné dans le formulaire.' },
    { label: 'Tarif communauté — Consommateur', value: '95', unit: '€/MWh', description: 'Tarif communauté par défaut appliqué aux consommateurs si non renseigné.' },
    { label: 'Tarif communauté — Producteur / Prosumer', value: '65', unit: '€/MWh', description: 'Tarif de rachat communauté par défaut appliqué aux producteurs si non renseigné.' },
    { label: 'Tarif de revente réseau — Producteur / Prosumer', value: '25', unit: '€/MWh', description: 'Prix de revente réseau par défaut appliqué aux producteurs si non renseigné.' },
  ],
  reductionReseau: [
    { label: 'Bruxelles — Type A (même bâtiment)', value: '80.47', unit: '€/MWh', description: 'Réduction sur les frais de réseau pour un partage au sein du même bâtiment à Bruxelles.' },
    { label: 'Bruxelles — Type B (même cabine Sibelga)', value: '46.59', unit: '€/MWh', description: 'Réduction sur les frais de réseau pour un partage au sein de la même cabine Sibelga.' },
    { label: 'Bruxelles — Type C (même poste Elia)', value: '21.44', unit: '€/MWh', description: 'Réduction sur les frais de réseau pour un partage au sein du même poste Elia.' },
    { label: 'Bruxelles — Type D (poste Elia différent)', value: '0', unit: '€/MWh', description: 'Aucune réduction sur les frais de réseau pour un partage entre postes Elia différents.' },
    { label: 'Wallonie — Même bâtiment', value: '108', unit: '€/MWh', description: 'Réduction sur les frais de réseau pour un partage au sein du même bâtiment en Wallonie.' },
    { label: 'Wallonie — Communauté d\'énergie', value: '0', unit: '€/MWh', description: 'Aucune réduction sur les frais de réseau pour une communauté d\'énergie en Wallonie.' },
  ],
};

interface IndexationRow {
  id: string;
  param_name: string;
  month_key: string;
  value: number;
  updated_at: string;
}

const FRENCH_MONTHS: Record<string, string> = {
  '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
  '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
  '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre',
};

function formatMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  return `${FRENCH_MONTHS[month] || month} ${year}`;
}

type AddMode = 'single' | 'range';

function generateMonthRange(start: string, end: string): string[] {
  if (!start || !end || start > end) return [];
  const months: string[] = [];
  let current = start;
  while (current <= end) {
    months.push(current);
    const [y, m] = current.split('-').map(Number);
    const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
    current = next;
  }
  return months;
}

function IndexationSection() {
  const [rows, setRows] = useState<IndexationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const [paramNames, setParamNames] = useState<string[]>([]);
  const [newParamName, setNewParamName] = useState('');
  const [newMonthKey, setNewMonthKey] = useState('');
  const [newRangeStart, setNewRangeStart] = useState('');
  const [newRangeEnd, setNewRangeEnd] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [newCustomParam, setNewCustomParam] = useState('');
  const [selectedParam, setSelectedParam] = useState<string>('all');
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
  const [addMode, setAddMode] = useState<AddMode>('single');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('indexation_parameters')
        .select('*')
        .order('param_name')
        .order('month_key');
      if (error) throw error;
      const r = data || [];
      setRows(r);
      const vals: Record<string, string> = {};
      for (const row of r) vals[row.id] = String(row.value);
      setEditValues(vals);
      setDirtyIds(new Set());
      const names = Array.from(new Set(r.map((x: IndexationRow) => x.param_name)));
      setParamNames(names);
    } catch {
      toast.error('Erreur lors du chargement des paramètres d\'indexation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (id: string, val: string) => {
    setEditValues(prev => ({ ...prev, [id]: val }));
    const original = rows.find(r => r.id === id);
    const parsed = parseFloat(val);
    if (original && !isNaN(parsed) && parsed !== original.value) {
      setDirtyIds(prev => new Set(prev).add(id));
    } else {
      setDirtyIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const handleSave = async (id: string) => {
    const parsed = parseFloat(editValues[id]);
    if (isNaN(parsed)) { toast.error('Valeur invalide'); return; }
    setSaving(id);
    try {
      const { error } = await supabase
        .from('indexation_parameters')
        .update({ value: parsed, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      setRows(prev => prev.map(r => r.id === id ? { ...r, value: parsed } : r));
      setDirtyIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      toast.success('Valeur mise à jour');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer cette valeur ?')) return;
    try {
      const { error } = await supabase.from('indexation_parameters').delete().eq('id', id);
      if (error) throw error;
      setRows(prev => prev.filter(r => r.id !== id));
      toast.success('Valeur supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAdd = async () => {
    const paramToUse = newCustomParam.trim() || newParamName;
    if (!paramToUse) { toast.error('Veuillez indiquer un paramètre'); return; }
    const parsed = parseFloat(newValue.replace(',', '.'));
    if (isNaN(parsed)) { toast.error('Valeur invalide'); return; }

    let monthsToInsert: string[] = [];
    if (addMode === 'single') {
      if (!newMonthKey) { toast.error('Veuillez sélectionner un mois'); return; }
      monthsToInsert = [newMonthKey];
    } else {
      if (!newRangeStart || !newRangeEnd) { toast.error('Veuillez sélectionner la plage de mois'); return; }
      if (newRangeStart > newRangeEnd) { toast.error('Le mois de début doit être avant le mois de fin'); return; }
      monthsToInsert = generateMonthRange(newRangeStart, newRangeEnd);
      if (monthsToInsert.length === 0) { toast.error('Plage de mois invalide'); return; }
      if (monthsToInsert.length > 60) { toast.error('Plage trop grande (max 60 mois)'); return; }
    }

    setAddingNew(true);
    try {
      const upsertData = monthsToInsert.map(mk => ({
        param_name: paramToUse,
        month_key: mk,
        value: parsed,
        updated_at: new Date().toISOString()
      }));
      const { error } = await supabase
        .from('indexation_parameters')
        .upsert(upsertData, { onConflict: 'param_name,month_key' });
      if (error) throw error;
      await load();
      setNewMonthKey('');
      setNewRangeStart('');
      setNewRangeEnd('');
      setNewValue('');
      setNewCustomParam('');
      setShowAddForm(false);
      toast.success(monthsToInsert.length > 1
        ? `${monthsToInsert.length} valeurs ajoutées / mises à jour`
        : 'Valeur ajoutée / mise à jour');
    } catch (err: any) {
      console.error('Erreur ajout indexation:', err);
      toast.error(err?.message || 'Erreur lors de l\'ajout');
    } finally {
      setAddingNew(false);
    }
  };

  const filteredRows = selectedParam === 'all' ? rows : rows.filter(r => r.param_name === selectedParam);
  const groupedRows = filteredRows.reduce<Record<string, IndexationRow[]>>((acc, r) => {
    if (!acc[r.param_name]) acc[r.param_name] = [];
    acc[r.param_name].push(r);
    return acc;
  }, {});

  const toggleYear = (key: string) => {
    setExpandedYears(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const rangePreview = addMode === 'range' && newRangeStart && newRangeEnd && newRangeStart <= newRangeEnd
    ? generateMonthRange(newRangeStart, newRangeEnd)
    : [];

  return (
    <div className="border border-cyan-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-cyan-50 border-cyan-200 transition-colors hover:brightness-[0.97]"
      >
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-neutral-800">Paramètres d'indexation</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-cyan-100 text-cyan-800">{rows.length} valeurs</span>
            </div>
            {expanded && (
              <p className="text-xs text-neutral-500 mt-0.5">
                Valeurs mensuelles (Belpex, Endex 101, CREG…) utilisées dans les formules variables de prix.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expanded && (
            <span className="text-xs font-mono text-neutral-500 hidden md:block bg-white/70 px-2 py-1 rounded-lg border border-white/50">
              Prix = A × X
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
        </div>
      </button>

      {expanded && (
        <div className="bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-4 border-neutral-200 border-t-cyan-500 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-neutral-500">Paramètre :</label>
                  <select
                    value={selectedParam}
                    onChange={e => setSelectedParam(e.target.value)}
                    className="text-sm border border-neutral-200 rounded-lg px-2 py-1.5 bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                  >
                    <option value="all">Tous</option>
                    {paramNames.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <button
                  onClick={() => setShowAddForm(v => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a3a2a] hover:bg-[#22c55e] text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter des valeurs
                </button>
              </div>

              {/* Add form */}
              {showAddForm && (
                <div className="px-4 py-4 bg-cyan-50 border-b border-cyan-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-cyan-700 flex items-center gap-1.5">
                      <Plus className="w-3.5 h-3.5" /> Ajouter / mettre à jour
                    </p>
                    {/* Mode toggle */}
                    <div className="flex bg-white border border-neutral-200 rounded-lg overflow-hidden text-xs font-semibold">
                      <button
                        onClick={() => setAddMode('single')}
                        className={`px-3 py-1.5 transition-colors ${addMode === 'single' ? 'bg-cyan-500 text-white' : 'text-neutral-600 hover:bg-neutral-50'}`}
                      >
                        Mois unique
                      </button>
                      <button
                        onClick={() => setAddMode('range')}
                        className={`px-3 py-1.5 transition-colors ${addMode === 'range' ? 'bg-cyan-500 text-white' : 'text-neutral-600 hover:bg-neutral-50'}`}
                      >
                        Plage de mois
                      </button>
                    </div>
                  </div>

                  {/* Param selection */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-neutral-600 block mb-1">Paramètre existant</label>
                      <select
                        value={newParamName}
                        onChange={e => { setNewParamName(e.target.value); setNewCustomParam(''); }}
                        className="w-full text-sm border border-neutral-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-300"
                      >
                        {paramNames.length === 0 && <option value="">Aucun paramètre</option>}
                        {paramNames.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-neutral-600 block mb-1">Nouveau paramètre (ou laisser vide)</label>
                      <input
                        type="text"
                        placeholder="ex: CREG, Endex 201…"
                        value={newCustomParam}
                        onChange={e => setNewCustomParam(e.target.value)}
                        className="w-full text-sm border border-neutral-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-300"
                      />
                    </div>
                  </div>

                  {/* Month / range selection */}
                  {addMode === 'single' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-neutral-600 block mb-1">Mois</label>
                        <input
                          type="month"
                          value={newMonthKey}
                          onChange={e => setNewMonthKey(e.target.value)}
                          className="w-full text-sm border border-neutral-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-300"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-neutral-600 block mb-1">Valeur (€/MWh)</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="ex: 108.52"
                            value={newValue}
                            onChange={e => setNewValue(e.target.value)}
                            className="flex-1 text-sm border border-neutral-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-300"
                          />
                          <button
                            onClick={handleAdd}
                            disabled={addingNew}
                            className="flex items-center gap-1 px-3 py-2 bg-[#1a3a2a] hover:bg-[#22c55e] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 flex-shrink-0"
                          >
                            {addingNew ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            {!addingNew && 'Ajouter'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Mois de début</label>
                          <input
                            type="month"
                            value={newRangeStart}
                            onChange={e => setNewRangeStart(e.target.value)}
                            className="w-full text-sm border border-neutral-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-300"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Mois de fin</label>
                          <input
                            type="month"
                            value={newRangeEnd}
                            onChange={e => setNewRangeEnd(e.target.value)}
                            className="w-full text-sm border border-neutral-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-300"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Valeur unique (€/MWh)</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="ex: 108.52"
                              value={newValue}
                              onChange={e => setNewValue(e.target.value)}
                              className="flex-1 text-sm border border-neutral-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-300"
                            />
                            <button
                              onClick={handleAdd}
                              disabled={addingNew}
                              className="flex items-center gap-1 px-3 py-2 bg-[#1a3a2a] hover:bg-[#22c55e] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 flex-shrink-0"
                            >
                              {addingNew ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                              {!addingNew && 'Appliquer'}
                            </button>
                          </div>
                        </div>
                      </div>
                      {rangePreview.length > 0 && (
                        <div className="bg-white border border-cyan-200 rounded-lg px-3 py-2">
                          <p className="text-xs font-semibold text-cyan-700 mb-1.5">{rangePreview.length} mois sélectionnés :</p>
                          <div className="flex flex-wrap gap-1">
                            {rangePreview.map(mk => (
                              <span key={mk} className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-medium">
                                {formatMonthKey(mk)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-cyan-600">
                    Si un couple paramètre + mois existe déjà, la valeur sera mise à jour.
                    {newCustomParam.trim() ? ` Le nouveau paramètre "${newCustomParam.trim()}" sera créé.` : ''}
                  </p>
                </div>
              )}

              {/* Table by param group, then by year */}
              <div className="divide-y divide-neutral-100">
                {Object.entries(groupedRows).map(([paramName, paramRows]) => {
                  const rowsByYear = paramRows.reduce<Record<string, IndexationRow[]>>((acc, r) => {
                    const year = r.month_key.split('-')[0];
                    if (!acc[year]) acc[year] = [];
                    acc[year].push(r);
                    return acc;
                  }, {});
                  const sortedYears = Object.keys(rowsByYear).sort((a, b) => b.localeCompare(a));

                  return (
                    <div key={paramName}>
                      <div className="px-4 py-2 bg-neutral-50 flex items-center gap-2">
                        <TrendingUp className="w-3.5 h-3.5 text-cyan-600" />
                        <span className="text-xs font-bold text-neutral-700 uppercase tracking-wide">{paramName}</span>
                        <span className="text-xs text-neutral-400">{paramRows.length} valeurs</span>
                      </div>
                      <div className="divide-y divide-neutral-100">
                        {sortedYears.map(year => {
                          const yearKey = `${paramName}-${year}`;
                          const isYearExpanded = expandedYears.has(yearKey);
                          const yearRows = rowsByYear[year];
                          const hasDirty = yearRows.some(r => dirtyIds.has(r.id));

                          return (
                            <div key={year}>
                              <button
                                onClick={() => toggleYear(yearKey)}
                                className="w-full px-4 py-2.5 flex items-center justify-between bg-white hover:bg-neutral-50 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-neutral-700">{year}</span>
                                  <span className="text-xs text-neutral-400">{yearRows.length} mois</span>
                                  {hasDirty && <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">modifié</span>}
                                </div>
                                {isYearExpanded ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                              </button>
                              {isYearExpanded && (
                                <div className="divide-y divide-neutral-50">
                                  {yearRows.map(row => {
                                    const isDirty = dirtyIds.has(row.id);
                                    const isSavingThis = saving === row.id;
                                    const val = editValues[row.id] ?? String(row.value);
                                    return (
                                      <div key={row.id} className={`px-4 py-3 flex items-center gap-3 ${isDirty ? 'bg-amber-50/40' : 'bg-white'}`}>
                                        <span className="text-sm text-neutral-700 w-40 flex-shrink-0 font-medium">
                                          {formatMonthKey(row.month_key)}
                                        </span>
                                        <div className="flex items-center gap-2 flex-1">
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={val}
                                            onChange={e => handleChange(row.id, e.target.value)}
                                            className={`w-28 text-right text-sm font-mono border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 transition-all
                                              ${isDirty
                                                ? 'border-amber-400 bg-amber-50 focus:ring-amber-300 text-amber-900'
                                                : 'border-neutral-200 bg-neutral-50 focus:ring-neutral-300 text-neutral-800'
                                              }`}
                                          />
                                          <span className="text-xs text-neutral-400">€/MWh</span>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                          {isDirty && (
                                            <>
                                              <button
                                                onClick={() => { setEditValues(prev => ({ ...prev, [row.id]: String(row.value) })); setDirtyIds(prev => { const n = new Set(prev); n.delete(row.id); return n; }); }}
                                                className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                                                title="Annuler"
                                              >
                                                <RotateCcw className="w-3.5 h-3.5" />
                                              </button>
                                              <button
                                                onClick={() => handleSave(row.id)}
                                                disabled={isSavingThis}
                                                className="flex items-center gap-1 px-2.5 py-1.5 bg-[#1a3a2a] hover:bg-[#22c55e] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
                                              >
                                                {isSavingThis ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-3 h-3" />}
                                                {!isSavingThis && 'Sauvegarder'}
                                              </button>
                                            </>
                                          )}
                                          {!isDirty && <CheckCircle className="w-4 h-4 text-neutral-300" />}
                                          <button
                                            onClick={() => handleDelete(row.id)}
                                            className="p-1.5 text-neutral-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-1"
                                            title="Supprimer"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {filteredRows.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-neutral-400">
                    Aucune valeur d'indexation trouvée.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SimulationFixedParams() {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-neutral-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-neutral-50 border-neutral-200 transition-colors hover:brightness-[0.97]"
      >
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-neutral-400" />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-neutral-800">Paramètres fixes — Simulateur CRM</span>
              <span className="flex items-center gap-1 text-[10px] font-semibold bg-neutral-200 text-neutral-600 px-2 py-0.5 rounded-full">
                <Lock className="w-2.5 h-2.5" />
                Lecture seule
              </span>
            </div>
            {expanded && (
              <p className="text-xs text-neutral-500 mt-0.5">Taux et tarifs hardcodés dans le simulateur CRM. Non modifiables depuis l'interface — nécessitent une mise à jour du code source.</p>
            )}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
      </button>

      {expanded && (
        <div className="divide-y divide-neutral-100">
          <div className="px-4 py-3 bg-white">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3">Tarifs par défaut (si non saisis par le participant)</p>
            <div className="space-y-2">
              {SIMULATION_PARAMS.tarifDefaut.map(p => (
                <div key={p.label} className="flex items-center justify-between gap-4 py-2.5 px-3 rounded-lg bg-neutral-50 border border-neutral-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-800">{p.label}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{p.description}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-mono font-bold text-neutral-700 bg-white border border-neutral-200 px-3 py-1.5 rounded-lg min-w-[80px] text-right">{p.value}</span>
                    <span className="text-xs text-neutral-400 font-medium w-14">{p.unit}</span>
                    <Lock className="w-3.5 h-3.5 text-neutral-300 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-4 py-3 bg-white">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Réduction frais réseau selon le type de partage</p>
            <p className="text-xs text-neutral-400 mb-3">Appliquée aux consommateurs uniquement, proportionnellement à l'énergie partagée qu'ils reçoivent.</p>
            <div className="space-y-2">
              {SIMULATION_PARAMS.reductionReseau.map(p => (
                <div key={p.label} className="flex items-center justify-between gap-4 py-2.5 px-3 rounded-lg bg-neutral-50 border border-neutral-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-800">{p.label}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{p.description}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-sm font-mono font-bold px-3 py-1.5 rounded-lg border min-w-[80px] text-right ${parseFloat(p.value) > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-neutral-400 bg-white border-neutral-200'}`}>
                      {parseFloat(p.value) > 0 ? `−${p.value}` : p.value}
                    </span>
                    <span className="text-xs text-neutral-400 font-medium w-14">{p.unit}</span>
                    <Lock className="w-3.5 h-3.5 text-neutral-300 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function FormulasModal({ isOpen, onClose }: FormulasModalProps) {
  const [config, setConfig] = useState<BillingConfigRow[]>([]);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(SECTION_ORDER));

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('billing_config')
        .select('*')
        .order('section')
        .order('key');
      if (error) throw error;
      setConfig(data || []);
      const vals: Record<string, string> = {};
      for (const row of data || []) {
        vals[row.key] = String(row.value);
      }
      setEditValues(vals);
      setDirtyKeys(new Set());
    } catch {
      toast.error('Erreur lors du chargement des formules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadConfig();
  }, [isOpen, loadConfig]);

  const handleChange = (key: string, val: string) => {
    setEditValues(prev => ({ ...prev, [key]: val }));
    const original = config.find(r => r.key === key);
    const parsed = parseFloat(val);
    if (original && !isNaN(parsed) && parsed !== original.value) {
      setDirtyKeys(prev => new Set(prev).add(key));
    } else {
      setDirtyKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleSave = async (key: string) => {
    const parsed = parseFloat(editValues[key]);
    if (isNaN(parsed)) {
      toast.error('Valeur invalide');
      return;
    }
    setSaving(key);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('billing_config')
        .update({ value: parsed, updated_at: new Date().toISOString(), updated_by: user?.email || '' })
        .eq('key', key);
      if (error) throw error;
      setConfig(prev => prev.map(r => r.key === key ? { ...r, value: parsed, updated_at: new Date().toISOString(), updated_by: user?.email || '' } : r));
      setDirtyKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      toast.success('Valeur mise à jour');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = (key: string) => {
    const original = config.find(r => r.key === key);
    if (!original) return;
    setEditValues(prev => ({ ...prev, [key]: String(original.value) }));
    setDirtyKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const grouped = SECTION_ORDER.reduce<Record<string, BillingConfigRow[]>>((acc, s) => {
    acc[s] = config.filter(r => r.section === s);
    return acc;
  }, {});

  const totalDirty = dirtyKeys.size;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1a3a2a] flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-neutral-900">Formules &amp; Taux</h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {totalDirty > 0 && (
              <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full">
                {totalDirty} modification{totalDirty > 1 ? 's' : ''} non sauvegardée{totalDirty > 1 ? 's' : ''}
              </span>
            )}
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 transition-colors p-1 rounded-lg hover:bg-neutral-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-neutral-200 border-t-[#1a3a2a] rounded-full animate-spin" />
            </div>
          ) : (
            SECTION_ORDER.map(section => {
              const rows = grouped[section];
              if (!rows || rows.length === 0) return null;
              const meta = SECTION_META[section];
              const colors = COLOR_CLASSES[meta.color];
              const isExpanded = expandedSections.has(section);
              const sectionDirty = rows.some(r => dirtyKeys.has(r.key));

              return (
                <div key={section} className={`border rounded-xl overflow-hidden ${colors.border}`}>
                  {/* Section header */}
                  <button
                    onClick={() => toggleSection(section)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 ${colors.header} transition-colors hover:brightness-[0.97]`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-700 font-bold text-neutral-800">{meta.label}</span>
                          {sectionDirty && (
                            <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">modifié</span>
                          )}
                        </div>
                        {isExpanded && (
                          <p className="text-xs text-neutral-500 mt-0.5">{meta.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {meta.formula && isExpanded && (
                        <span className="text-xs font-mono text-neutral-500 hidden md:block bg-white/70 px-2 py-1 rounded-lg border border-white/50">
                          {meta.formula}
                        </span>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                    </div>
                  </button>

                  {/* Rows */}
                  {isExpanded && (
                    <div className="divide-y divide-neutral-100">
                      {rows.map(row => {
                        const isDirty = dirtyKeys.has(row.key);
                        const isSavingThis = saving === row.key;
                        const val = editValues[row.key] ?? String(row.value);
                        const displayVal = row.unit === '%'
                          ? `${(row.value * 100).toFixed(row.value < 0.1 ? 0 : 2)} %`
                          : `${row.value} ${row.unit}`;

                        return (
                          <div key={row.key} className={`px-4 py-4 bg-white transition-colors ${isDirty ? 'bg-amber-50/40' : ''}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-neutral-800">{row.label}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge}`}>{row.unit}</span>
                                  {isDirty && <Edit3 className="w-3.5 h-3.5 text-amber-500" />}
                                </div>
                                <p className="text-xs text-neutral-500 leading-relaxed">{row.description}</p>
                                {row.updated_by && (
                                  <p className="text-xs text-neutral-400 mt-1">
                                    Mis à jour le {new Date(row.updated_at).toLocaleDateString('fr-BE')}
                                    {row.updated_by ? ` par ${row.updated_by}` : ''}
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="relative">
                                  <input
                                    type="number"
                                    step="0.0001"
                                    value={row.unit === '%' ? String(parseFloat(val) * 100) : val}
                                    onChange={e => {
                                      const raw = row.unit === '%'
                                        ? String(parseFloat(e.target.value) / 100)
                                        : e.target.value;
                                      handleChange(row.key, raw);
                                    }}
                                    className={`w-28 text-right text-sm font-mono border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all
                                      ${isDirty
                                        ? 'border-amber-400 bg-amber-50 focus:ring-amber-300 text-amber-900'
                                        : 'border-neutral-200 bg-neutral-50 focus:ring-neutral-300 text-neutral-800'
                                      }`}
                                  />
                                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-neutral-400 pointer-events-none select-none ml-1">
                                    {row.unit === '%' ? '%' : ''}
                                  </span>
                                </div>

                                {isDirty && (
                                  <>
                                    <button
                                      onClick={() => handleReset(row.key)}
                                      className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                                      title="Annuler la modification"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleSave(row.key)}
                                      disabled={isSavingThis}
                                      className="flex items-center gap-1.5 px-3 py-2 bg-[#1a3a2a] hover:bg-[#22c55e] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
                                    >
                                      {isSavingThis ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                      ) : (
                                        <Save className="w-3.5 h-3.5" />
                                      )}
                                      {isSavingThis ? 'Enregistrement...' : 'Sauvegarder'}
                                    </button>
                                  </>
                                )}

                                {!isDirty && (
                                  <div className="flex items-center justify-center w-8 h-8">
                                    <CheckCircle className="w-4 h-4 text-neutral-300" />
                                  </div>
                                )}
                              </div>
                            </div>

                            {isDirty && (
                              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 flex items-center gap-2">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                Valeur actuelle en base : <strong>{displayVal}</strong>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Indexation Parameters */}
          <IndexationSection />

          {/* Simulation CRM - Fixed Parameters */}
          <SimulationFixedParams />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex-shrink-0 flex items-center justify-between bg-neutral-50 rounded-b-2xl">
          <p className="text-xs text-neutral-500">
            Les modifications sont sauvegardées individuellement — cliquez sur "Sauvegarder" pour chaque ligne modifiée.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 text-sm font-medium rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
